import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { ThemeProvider } from '@blocksuite/affine/shared/services';
import { BlockStdScope, ShadowlessElement } from '@blocksuite/affine/std';
import type { ExtensionType, Store } from '@blocksuite/affine/store';
import { css, html, nothing, type TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';

export class EdgelessEditor extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    edgeless-editor {
      font-family: var(--affine-font-family);
      background: var(--affine-background-primary-color);
    }

    edgeless-editor * {
      box-sizing: border-box;
    }

    @media print {
      edgeless-editor {
        height: auto;
      }
    }

    .affine-edgeless-viewport {
      display: block;
      height: 100%;
      position: relative;
      overflow: clip;
      container-name: viewport;
      container-type: inline-size;
    }
  `;

  get host() {
    try {
      return this.std.host;
    } catch {
      return null;
    }
  }

  override connectedCallback() {
    super.connectedCallback();
    this._disposables.add(
      this.doc.slots.rootAdded.subscribe(() => this.requestUpdate())
    );
    this._createStd();
  }

  override disconnectedCallback() {
    // ✅ 确保在断开连接时清理 BlockStdScope
    this._disposeStd();
    super.disconnectedCallback();
  }

  private _createStd() {
    this.std = new BlockStdScope({
      store: this.doc,
      extensions: this.specs,
    });
  }

  private _disposeStd() {
    if (this.std) {
      try {
        this.std.unmount();
        // ✅ 清理 ServiceCachePool 中缓存的服务实例
        // 这些服务实例可能持有 BlockStdScope 的引用，导致内存泄漏
        if (this.std.provider && 'cache' in this.std.provider) {
          const provider = this.std.provider as { cache: { cache: Map<string, Map<string, any>> } };
          // 遍历缓存中的服务，调用 dispose 方法（如果有的话）
          provider.cache.cache.forEach(variantMap => {
            variantMap.forEach(service => {
              if (service && typeof service[Symbol.dispose] === 'function') {
                try {
                  service[Symbol.dispose]();
                } catch {
                  // ignore
                }
              }
            });
          });
          provider.cache.cache.clear();
        }
      } catch {
        // ignore
      }
    }
  }

  override async getUpdateComplete(): Promise<boolean> {
    const result = await super.getUpdateComplete();
    await this.host?.updateComplete;
    return result;
  }

  override render() {
    if (!this.doc.root) return nothing;

    const std = this.std;
    const theme = std.get(ThemeProvider).edgeless$.value;
    return html`
      <div class="affine-edgeless-viewport" data-theme=${theme}>
        ${guard([std], () => std.render())}
      </div>
    `;
  }

  override willUpdate(
    changedProperties: Map<string | number | symbol, unknown>
  ) {
    super.willUpdate(changedProperties);
    if (
      this.hasUpdated && // skip the first update
      changedProperties.has('doc')
    ) {
      // ✅ 先清理旧的 BlockStdScope，再创建新的
      this._disposeStd();
      this._createStd();
    }
  }

  @property({ attribute: false })
  accessor doc!: Store;

  @property({ attribute: false })
  accessor editor!: TemplateResult;

  @property({ attribute: false })
  accessor specs: ExtensionType[] = [];

  @state()
  accessor std!: BlockStdScope;
}

declare global {
  interface HTMLElementTagNameMap {
    'edgeless-editor': EdgelessEditor;
  }
}
